import { useEffect, useRef } from 'react'
import { GatewayChannelHandler, GatewayEvents } from '@shared/ipc/GatewayEvents'
import { GatewayServerChannel, GatewayServerPayload } from '@main/utils/gateway/types'

/**
 * Subscribe to gateway messages on a specific channel.
 * Messages are filtered by channel name and the callback receives only the payload.
 *
 * @param channel - The channel name to subscribe to (e.g., 'toast')
 * @param callback - Called with the message payload when a message arrives on the channel
 *
 * @example
 * useGatewayChannel('toast', (payload) => {
 *   console.log('Received toast notification:', payload)
 * })
 */
export function useGatewayChannel<C extends GatewayServerChannel>(
  channel: C,
  callback: (payload: GatewayServerPayload<C>) => void,
) {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    const handler = (payload: GatewayServerPayload<C>) => {
      callbackRef.current(payload)
    }
    return GatewayEvents.onChannel(channel, handler as GatewayChannelHandler)
  }, [channel])
}
